import { LanguageLabel, LanguageName } from "@axon-enterprise/i18n";
import { LanguageContext } from "@axon-enterprise/i18n-helpers";
import { Button, Dialog, Stack, Body1 } from "@axon-enterprise/spark";
import React, { useState, useEffect, useCallback, useMemo, useContext } from "react";

import { SUPPORTED_LANGUAGES } from "../../i18n/constants";

const LANGUAGE_ROW_COUNT = 2;

interface LanguageSelectorDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Language selector dialog that uses i18n-helpers context
 * No prop drilling needed - accesses language from LanguageContext
 */
const LanguageSelectorDialog: React.FC<LanguageSelectorDialogProps> = ({ 
  open, 
  onClose
}) => {
  const { language: currentLanguage, setLanguage } = useContext(LanguageContext);
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageName>(currentLanguage);

  const languageRows = useMemo(() => {
    if (!LanguageLabel || typeof LanguageLabel !== "object") {
      return [];
    }

    const sortedLanguages = Object.entries(LanguageLabel)
      .filter(([languageName]) => SUPPORTED_LANGUAGES.includes(languageName as any))
      .sort(([, labelA], [, labelB]) => labelA.localeCompare(labelB));

    return Array.from(
      { length: Math.ceil(sortedLanguages.length / LANGUAGE_ROW_COUNT) },
      (_, i) => {
        const start = i * LANGUAGE_ROW_COUNT;
        return sortedLanguages.slice(start, start + LANGUAGE_ROW_COUNT);
      }
    );
  }, []);

  useEffect(() => {
    if (open) {
      setSelectedLanguage(currentLanguage);
    }
  }, [open, currentLanguage]);


  const handleLanguageClick = useCallback((languageName: string) => {
    setSelectedLanguage(languageName as LanguageName);
    if (languageName !== currentLanguage) {
        setLanguage(languageName as LanguageName);
        localStorage.setItem("language", languageName);
        onClose();
    } else {
      onClose();
    }
  }, [
    setLanguage,
    currentLanguage,
    onClose,
  ]);

  const handleCancel = () => {
    setSelectedLanguage(currentLanguage);
    onClose();
  };

  return (
    <Dialog
      open={open}
      title={__("Select language")}
      onClose={handleCancel}
      primaryAction={undefined}
      secondaryAction={null}
    >
      <Stack space="m">
        <Body1>
          {__("The language you choose updates your $[productName] interface.", { 
              productName: <strong key="productName">fususCORE</strong>, 
          })}
        </Body1>
        <Stack space="xs" centered width="100%">
          {languageRows.map((row, rowIndex) => {
            const isFullRow = row.length === LANGUAGE_ROW_COUNT;
            return (
              <Stack key={rowIndex} direction="row" space="s">
                {row.map(([languageName, languageLabel]) => {
                  const isSelected = selectedLanguage === languageName;

                  return (
                    <Stack
                      key={languageName}
                      width={isFullRow ? "50%" : `${100 / LANGUAGE_ROW_COUNT}%`}
                    >
                      <div className="full-width-uploader-wrapper">
                        <Button
                          text={languageLabel}
                          onClick={() => handleLanguageClick(languageName)}
                          variant={
                            isSelected
                              ? Button.variants.PrimaryKey
                              : Button.variants.TertiaryKey
                          }
                        />
                      </div>
                    </Stack>
                  );
                })}
              </Stack>
            );
          })}
        </Stack>
      </Stack>
    </Dialog>
  );
};

LanguageSelectorDialog.displayName = "LanguageSelectorDialog";

export default LanguageSelectorDialog;
